import { useState } from "react";
import { useAuth } from "@/lib/authContext";
import { useLocation, Link } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { getSupabase } from "@/lib/supabase";

export default function Login() {
  const { signIn } = useAuth();
  const [, setLocation] = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [magicSent, setMagicSent] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setMagicSent(false);
    setLoading(true);

    try {
      const result = await signIn(email, password);
      if (result.error) {
        setError(result.error);
      } else {
        const redirect = sessionStorage.getItem("centcom_login_redirect");
        sessionStorage.removeItem("centcom_login_redirect");
        setLocation(redirect || "/");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    setError("");
    setMagicSent(false);
    setMagicLoading(true);

    try {
      const supabase = await getSupabase();
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setError(error.message);
      } else {
        setMagicSent(true);
      }
    } finally {
      setMagicLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a2744] via-[#1e2d4d] to-[#243554] p-4">
      <div className="w-full max-w-md space-y-8">
        <Card className="shadow-2xl border-0 bg-white overflow-hidden">
          <div className="flex items-center justify-center bg-white pt-10 pb-4 px-8">
            <img
              src="/logo.png"
              alt="Carpenter & Company"
              className="h-52 w-auto object-contain"
            />
          </div>
          <CardHeader className="text-center pt-0 pb-4">
            <CardTitle className="text-black text-xl font-bold">Welcome Back</CardTitle>
            <CardDescription className="text-gray-800">
              Sign in to your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-black font-semibold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@carpentercpas.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white border-gray-400 text-black placeholder:text-gray-500"
                  data-testid="input-email"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-black font-semibold">Password</Label>
                  <Link href="/forgot-password">
                    <span
                      className="text-sm text-[#1a2744] hover:text-[#c9a962] font-semibold cursor-pointer"
                      data-testid="link-forgot-password"
                    >
                      Forgot password?
                    </span>
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="bg-white border-gray-400 text-black placeholder:text-gray-500"
                  data-testid="input-password"
                />
              </div>

              {error && (
                <div
                  className="flex items-center gap-2 text-red-700 text-sm bg-red-50 p-3 rounded-md border border-red-200"
                  data-testid="text-error"
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {magicSent && (
                <div className="text-green-700 text-sm bg-green-50 p-3 rounded-md border border-green-200">
                  Magic link sent — check your email.
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-[#1a2744] hover:bg-[#243554] text-white font-medium py-5"
                disabled={loading}
                data-testid="button-submit"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </Button>

              <div className="text-center text-sm text-gray-600 font-medium">
                — or —
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full border-[#1a2744] text-[#1a2744] hover:bg-[#1a2744] hover:text-white font-medium py-5"
                disabled={magicLoading || !email}
                onClick={handleMagicLink}
                data-testid="button-magic-link"
              >
                {magicLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Email me a magic link
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-white/50 text-sm">
          Need an account? Contact your administrator.
        </p>
      </div>
    </div>
  );
}
